import React, { useState, useEffect } from 'react';
import { Bookmark, X, ExternalLink, FileText, BookOpen, Trash2, Play, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRealtime } from '../contexts/RealtimeContext';
import { getUserSavedQuestions, removeSavedQuestion, getUserBookmarks, removeBookmark, listenToUserBookmarks, listenToUserSavedQuestions, getUserQuestionBookmarks, removeQuestionBookmark, listenToUserQuestionBookmarks } from '../firebase/collections';
import { formatTimestamp } from '../utils/timestampUtils';
import { useNavigate } from 'react-router-dom';

const BookmarkDropdown = () => {
  const { currentUser } = useRealtime();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [questionBookmarks, setQuestionBookmarks] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('questions'); // 'questions', 'resources', 'notes'

  // Load bookmarks when dropdown opens
  useEffect(() => {
    if (isOpen && currentUser?.uid) {
      loadAllBookmarks();
    }
  }, [isOpen, currentUser?.uid]);

  // Real-time listener for bookmarks
  useEffect(() => {
    if (currentUser?.uid) {
      const unsubscribe = listenToUserBookmarks(currentUser.uid, (result) => {
        if (result.success) {
          setBookmarks(result.data);
        } else {
        }
      });

      return () => unsubscribe();
    }
  }, [currentUser?.uid]);

  // Real-time listener for question bookmarks
  useEffect(() => {
    if (currentUser?.uid) {
      const unsubscribe = listenToUserQuestionBookmarks(currentUser.uid, (result) => {
        // BookmarkDropdown: Received question bookmarks update
        if (result.success) {
          setQuestionBookmarks(result.data);
        }
      });

      return () => unsubscribe();
    }
  }, [currentUser?.uid]);

  // Real-time listener for saved questions (legacy)
  useEffect(() => {
    if (currentUser?.uid) {
      const unsubscribe = listenToUserSavedQuestions(currentUser.uid, (result) => {
        // BookmarkDropdown: Received saved questions update
        if (result.success) {
          setSavedQuestions(result.data);
        }
      });

      return () => unsubscribe();
    }
  }, [currentUser?.uid]);

  const loadAllBookmarks = async () => {
    try {
      setLoading(true);
      
      // Load question bookmarks (new system)
      const questionBookmarksResult = await getUserQuestionBookmarks(currentUser.uid, 10);
      if (questionBookmarksResult.success) {
        setQuestionBookmarks(questionBookmarksResult.data);
      }
      
      // Load old saved questions (legacy)
      const questionsResult = await getUserSavedQuestions(currentUser.uid, 10);
      if (questionsResult.success) {
        setSavedQuestions(questionsResult.data);
      }
      
      // Load resources and notes bookmarks
      const bookmarksResult = await getUserBookmarks(currentUser.uid);
      if (bookmarksResult.success) {
        setBookmarks(bookmarksResult.data);
        // Force a re-render by updating the state
        setBookmarks([...bookmarksResult.data]);
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      const result = await removeQuestionBookmark(questionId);
      if (result.success) {
        setQuestionBookmarks(prev => prev.filter(q => q.id !== questionId));
      }
    } catch (error) {
      // Error removing question bookmark
    }
  };

  const handleRemoveBookmark = async (bookmarkId) => {
    try {
      const result = await removeBookmark(bookmarkId);
      if (result.success) {
        setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
      }
    } catch (error) {
      // Error removing bookmark
    }
  };

  const handleStartPractice = () => {
    setIsOpen(false);
    navigate('/dashboard/saved-questions');
  };

  const handleViewAllBookmarks = () => {
    setIsOpen(false);
    navigate('/dashboard/bookmarks');
  };

  const getFilteredQuestions = () => {
    return questionBookmarks.slice(0, 5); // Show only 5 in dropdown
  };

  const getFilteredBookmarks = (type) => {
    const filtered = bookmarks.filter(b => b.type === type).slice(0, 3); // Show only 3 of each type
    return filtered;
  };

  const getTotalBookmarks = () => {
    return questionBookmarks.length + bookmarks.length;
  };

  const renderQuestionItem = (question) => (
    <div key={question.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg">
      <div className="flex-shrink-0">
        <BookOpen className="w-4 h-4 text-blue-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {question.question?.substring(0, 60)}...
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {question.topic} • {question.difficulty}
        </p>
      </div>
      <button
        onClick={() => handleRemoveQuestion(question.id)}
        className="flex-shrink-0 p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );

  const renderBookmarkItem = (bookmark) => (
    <div key={bookmark.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg">
      <div className="flex-shrink-0">
        {bookmark.type === 'resource' ? (
          <FileText className="w-4 h-4 text-green-600" />
        ) : (
          <BookOpen className="w-4 h-4 text-purple-600" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {bookmark.title}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {bookmark.description?.substring(0, 40)}...
        </p>
      </div>
      <div className="flex-shrink-0 flex space-x-1">
        {bookmark.url && (
          <a
            href={bookmark.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
          >
            <ExternalLink size={12} />
          </a>
        )}
        <button
          onClick={() => handleRemoveBookmark(bookmark.id)}
          className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Bookmark Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
        title="Bookmarks"
      >
        <Bookmark size={18} />
        {getTotalBookmarks() > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
            {getTotalBookmarks()}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Mobile overlay backdrop */}
            <div 
              className="fixed inset-0 bg-black/20 z-40 sm:hidden"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 sm:left-auto top-16 sm:top-full sm:mt-2 sm:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-[calc(100vh-8rem)] flex flex-col"
            >
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Bookmarks</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('questions')}
                className={`flex-1 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                  activeTab === 'questions'
                    ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Questions ({savedQuestions.length})
              </button>
              <button
                onClick={() => setActiveTab('resources')}
                className={`flex-1 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                  activeTab === 'resources'
                    ? 'text-green-600 border-b-2 border-green-600 dark:text-green-400 dark:border-green-400'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Resources ({getFilteredBookmarks('resource').length})
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`flex-1 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                  activeTab === 'notes'
                    ? 'text-purple-600 border-b-2 border-purple-600 dark:text-purple-400 dark:border-purple-400'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Notes ({getFilteredBookmarks('note').length})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading bookmarks...</p>
                </div>
              ) : (
                <div className="p-2 sm:p-2">
                  {activeTab === 'questions' && (
                    <div>
                      {getFilteredQuestions().length === 0 ? (
                        <div className="text-center py-6">
                          <BookOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No saved questions</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {getFilteredQuestions().map(renderQuestionItem)}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'resources' && (
                    <div>
                      {getFilteredBookmarks('resource').length === 0 ? (
                        <div className="text-center py-6">
                          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No saved resources</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {getFilteredBookmarks('resource').map(renderBookmarkItem)}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'notes' && (
                    <div>
                      {getFilteredBookmarks('note').length === 0 ? (
                        <div className="text-center py-6">
                          <BookOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No saved notes</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {getFilteredBookmarks('note').map(renderBookmarkItem)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-2 sm:p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex-shrink-0">
              <div className="flex space-x-2">
                {activeTab === 'questions' && savedQuestions.length > 0 && (
                  <button
                    onClick={handleStartPractice}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Play className="w-4 h-4 mr-1 inline" />
                    Practice
                  </button>
                )}
                <button
                  onClick={handleViewAllBookmarks}
                  className="flex-1 px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
                >
                  View All
                </button>
              </div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookmarkDropdown;
